import { useCallback, useEffect, useMemo } from 'react';
import { captureException } from '@sentry/nextjs';
import { Tooltip } from 'react-tooltip';
import { MY_COACH_PLAYERS_PAGE, getVideoAnalysisPageUrl } from 'constants/pages';
import { EMPTY_AVATAR_SRC } from 'constants/user';
import {
  FileFormatEnum,
  GetCoachAllPlayersVideoAnalysisQuery,
  VideoAnalysisReviewStatusesEnum,
  useGetCoachAllPlayersVideoAnalysisLazyQuery,
} from 'types/generated/client';
import { useViewer } from 'hooks/useViewer';
import AlertIcon from 'svg/AlertIcon';
import ArrowRight from 'svg/ArrowRight';
import Check from 'svg/Check';
import VideoCamera from 'svg/VideoCamera';
import { getDifferenceInHours } from 'screens/CoachPlayersTab/utils';
import { getFormattedSkills } from 'screens/VideoAnalysis/utils';
import { ButtonLink } from 'components/Button';
import CustomTable from 'components/CustomTable';
import { CustomTableProps } from 'components/CustomTable/props';
import Pill from 'components/Pill';
import { formatCreationWithIntl } from 'components/Premium/utils';
import classNames from 'styles/utils/classNames';
import { OutstandingVideoAnalysisSkeleton } from '../Skeletons';

const ALERT_THRESHOLD_HOURS = 72; // 3 days

type VideoAnalysisItem = NonNullable<GetCoachAllPlayersVideoAnalysisQuery['videoAnalysis']>[0];

const OutstandingVideoAnalysis = () => {
  const viewer = useViewer();

  const [
    getCoachAllPlayersVideoAnalysis,
    { loading: coachAllPlayersVideoAnalysisLoading, data: coachAllPlayersVideoAnalysisData },
  ] = useGetCoachAllPlayersVideoAnalysisLazyQuery();

  const getAllPlayersVideoAnalysis = useCallback(async () => {
    try {
      await getCoachAllPlayersVideoAnalysis({
        variables: {
          reviewerId: viewer.userId,
        },
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'network-only',
      });
    } catch (err) {
      captureException(err);
    }
  }, [viewer.userId, getCoachAllPlayersVideoAnalysis]);

  useEffect(() => {
    if (viewer.userId) {
      getAllPlayersVideoAnalysis();
    }
  }, [viewer.userId, getAllPlayersVideoAnalysis]);

  const videoAnalysisData = useMemo(() => {
    return coachAllPlayersVideoAnalysisData?.videoAnalysis || [];
  }, [coachAllPlayersVideoAnalysisData]);

  const columns: CustomTableProps<VideoAnalysisItem>['columns'] = [
    {
      title: 'Elapsed',
      columnClassName: 'w-[4rem]',
      render: (row) => {
        const differenceInHours = getDifferenceInHours(row.createdAt);
        return (
          <div className="pl-2">
            {row.reviewStatus === VideoAnalysisReviewStatusesEnum.Reviewed ? (
              <Check className="h-5 w-5 text-brand-green-300" />
            ) : differenceInHours > ALERT_THRESHOLD_HOURS ? (
              <>
                <Tooltip
                  className="!rounded-xl !bg-brand-fire-500"
                  id={`alert-icon-tip-${row.id}`}
                  content={`This player has been waiting for more than ${Math.floor(
                    differenceInHours / 24,
                  )} days. Please provide feedback as quickly as possible!`}
                  place="top"
                />
                <AlertIcon
                  className="text-color-brand-primary"
                  data-tooltip-id={`alert-icon-tip-${row.id}`}
                />
              </>
            ) : (
              <span className="typography-product-caption text-color-text-lightmode-tertiary dark:text-color-text-darkmode-tertiary">
                {differenceInHours}h
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: 'Video',
      columnClassName: 'max-md:hidden w-[9rem]',
      render: (row) => {
        const media = row.videoAnalysisFiles;
        if (
          media.fileFormat === FileFormatEnum.Video ||
          media.fileFormat === FileFormatEnum.Audio
        ) {
          return (
            <video
              controls
              className={classNames(
                'w-full rounded-xl object-cover',
                media.fileFormat === FileFormatEnum.Audio ? 'h-10' : 'h-20',
              )}
            >
              <source src={media.providerUrl} />
              Your browser does not support the video tag.
            </video>
          );
        } else {
          return <img src={media.providerUrl} className="h-20 w-full rounded-xl object-cover" />;
        }
      },
    },
    {
      title: 'Player',
      columnClassName: 'w-[16rem] max-sm:w-[10rem]',
      render: (row) => (
        <div className={classNames('flex items-center gap-1')}>
          <img
            className="h-8 w-8 rounded-full"
            src={row.videoAnalysisSubmission?.profileImageProviderUrl || EMPTY_AVATAR_SRC}
            alt="avatar"
          />
          <span className="typography-product-chips-filters capitalize">
            {row.videoAnalysisSubmission?.fullName}
          </span>
        </div>
      ),
    },
    {
      title: 'Date',
      columnClassName: 'w-[8rem] max-sm:hidden',
      render: (row) => (
        <span className="typography-product-chips-filters whitespace-nowrap text-color-text-lightmode-secondary dark:text-color-text-lightmode-secondary">
          {formatCreationWithIntl(row.createdAt, false)}
        </span>
      ),
    },
    {
      title: 'Skills Mentioned',
      columnClassName: 'max-lg:hidden',
      render: (row) => {
        const formattedSkills = getFormattedSkills(row?.videoAnalysisRequestedSkillsReviews).slice(
          0,
          3,
        );
        return (
          <div className="flex flex-wrap gap-1">
            {formattedSkills.map((skill, index) => (
              <Pill className="text-center" size="sm" key={`${skill}-${index}`} text={skill} />
            ))}
          </div>
        );
      },
    },
    {
      title: '',
      columnClassName: 'w-[8rem]',
      render: (row) => {
        const isFeedbackPending = row.reviewStatus === VideoAnalysisReviewStatusesEnum.Pending;
        return (
          <ButtonLink
            variant={isFeedbackPending ? 'brand' : 'secondary'}
            className="ml-auto w-full max-w-fit"
            size="sm"
            href={getVideoAnalysisPageUrl(row.id)}
            iconLeft={
              isFeedbackPending ? (
                <VideoCamera className="h-3 w-3 text-color-text-lightmode-invert max-sm:hidden sm:h-4 sm:w-4" />
              ) : (
                <ArrowRight className="h-3 w-3 sm:h-4 sm:w-4" />
              )
            }
          >
            {isFeedbackPending ? 'Analyze' : 'Details'}
          </ButtonLink>
        );
      },
    },
  ];

  if (coachAllPlayersVideoAnalysisLoading) {
    return <OutstandingVideoAnalysisSkeleton />;
  }

  return (
    <div className="container mx-auto mt-8 max-sm:mt-ds-xl  max-sm:px-4">
      <h5 className="typography-product-heading-compact-desktop max-sm:typography-product-subheading mb-ds-xl max-sm:mb-ds-md">
        Outstanding Video Lesson Reviews
      </h5>

      {videoAnalysisData.length > 0 ? (
        <>
          <CustomTable columns={columns} data={videoAnalysisData.slice(0, 5)} shadow={true} />
          <div className="mt-6 flex justify-end max-sm:mt-4">
            <ButtonLink
              href={MY_COACH_PLAYERS_PAGE}
              variant="secondary"
              sizeDesktop="md"
              sizeMobile="sm"
              className="w-fit"
            >
              See More
            </ButtonLink>
          </div>
        </>
      ) : (
        <div className="flex h-32 items-center justify-center rounded-2xl border border-color-border-card-lightmode dark:border-color-border-card-darkmode">
          <div className="typography-product-body-text text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
            No outstanding video lesson reviews
          </div>
        </div>
      )}
    </div>
  );
};

export default OutstandingVideoAnalysis;
